<script setup>
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import Icon from '@/Components/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useLocaleFormatting } from '@/composables/useLocaleFormatting';
import { getTenantSetting } from '@/composables/useTenant';
import { useSelectableMapLayers } from '@/composables/useSelectableMapLayers';
import { useVMapInstance } from '@/composables/useVMapInstance';
import { LEAFLET_CONFIG } from '@/utils/map';
import {
    getAvailablePompeiMapLayerOptions,
    getPompeiMapLayerConfig,
    POMPEI_DEFAULT_MAP_LAYER_KEY,
    resolvePompeiMapLayerKey,
} from '@/themes/Pompei/utils/mapLayerOptions';

const props = defineProps({
    data: Object,
    sheet: Object,
    tab: Object,
    active: {
        type: Boolean,
        default: true,
    },
});

const { t } = useI18n({ useScope: 'global' });
const { formatDate: formatLocalizedDate, formatNumber: formatLocalizedNumber } = useLocaleFormatting();

// Severity-first display order for the classification legend.
const CLASSIFICATION_ORDER = [
    'ia_5',
    'ia_4',
    'ia_3',
    'ia_2',
    'ia_1',
    'no_degradation',
    'dr_null',
    'not_classified',
    'not_monitored',
];

// Leaflet fitBounds padding in pixels; VMap's Leaflet fitBounds prop does not expose fit options.
const MONITORING_BOUNDS_FIT_PADDING = [0, 0];

// Large payload: keep it out of Vue's deep reactivity on purpose.
const payload = shallowRef(null);
const loading = ref(false);
const error = ref('');
const hasFetched = ref(false);
const selectedElementId = ref(null);
const activeClassificationKeys = ref(new Set());
const hasFittedVisibleBounds = ref(false);

const mapEl = shallowRef(null);
const containerEl = shallowRef(null);
const { invalidateSize, fitToGeoJsonBounds } = useVMapInstance(mapEl);

let resizeObserver = null;
let refreshFrameId = 0;
let removeWheelBlocker = null;

const compositeId = computed(() => props.data?.og_lrt_id ?? null);
const referent = computed(() => payload.value?.referent ?? null);
const summary = computed(() => payload.value?.summary ?? null);
const elements = computed(() => payload.value?.elements ?? []);

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

async function fetchSummary() {
    if (!compositeId.value || loading.value) {
        return;
    }

    loading.value = true;
    error.value = '';

    try {
        const response = await axios.get(
            `/api/pompei/topographic-referents/${encodeURIComponent(compositeId.value)}/status-summary`,
        );

        payload.value = response.data?.data ?? null;
        hasFittedVisibleBounds.value = false;
        selectedElementId.value = null;
        activeClassificationKeys.value = new Set();

        await nextTick();
        scheduleMapRefresh({ fitBounds: true });
    } catch {
        error.value = 'pompei.monitoring.errors.load';
    } finally {
        loading.value = false;
        hasFetched.value = true;
    }
}

function ensureFetched() {
    if (!hasFetched.value && !loading.value) {
        fetchSummary();
    }
}

// ---------------------------------------------------------------------------
// Classification helpers
// ---------------------------------------------------------------------------

function classificationOrderIndex(key) {
    const index = CLASSIFICATION_ORDER.indexOf(key);
    return index === -1 ? CLASSIFICATION_ORDER.length : index;
}

function elementClassificationKey(element) {
    return element?.status?.classification?.key ?? 'not_monitored';
}

const classificationDescriptions = computed(() => Object.fromEntries(
    (payload.value?.legend?.element_classifications ?? [])
        .map((entry) => [entry.key, entry.description]),
));

const classificationCounts = computed(() => {
    return [...(summary.value?.classification_counts ?? [])]
        .filter((entry) => Number(entry?.count ?? 0) > 0)
        .sort((a, b) => classificationOrderIndex(a.key) - classificationOrderIndex(b.key));
});

const hasActiveFilters = computed(() => activeClassificationKeys.value.size > 0);

function toggleClassificationFilter(key) {
    const next = new Set(activeClassificationKeys.value);
    if (next.has(key)) {
        next.delete(key);
    } else {
        next.add(key);
    }
    activeClassificationKeys.value = next;
}

function clearClassificationFilters() {
    activeClassificationKeys.value = new Set();
}

// ---------------------------------------------------------------------------
// Map layers
// ---------------------------------------------------------------------------

const selectedLayerKey = ref(resolvePompeiMapLayerKey(POMPEI_DEFAULT_MAP_LAYER_KEY));
const layerOptions = computed(() => getAvailablePompeiMapLayerOptions());

const {
    layerEntries,
    mapLayers,
    selectedLayerIndex,
    changeLayerByIndex,
} = useSelectableMapLayers({
    layerOptions,
    selectedLayerKey,
    resolveLayerKey: resolvePompeiMapLayerKey,
    getLayerConfig: getPompeiMapLayerConfig,
});

const showLayerControl = computed(() => layerEntries.value.length > 1);

const mapCenter = computed(() => {
    try {
        const raw = getTenantSetting('map.center');
        return raw ? JSON.parse(raw) : LEAFLET_CONFIG.center;
    } catch {
        return LEAFLET_CONFIG.center;
    }
});

const leafletConfig = computed(() => ({
    ...LEAFLET_CONFIG,
    preferCanvas: true,
    fitBoundsPadding: 0,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    touchZoom: false,
    boxZoom: false,
    keyboard: false,
    dragging: false,
}));

// ---------------------------------------------------------------------------
// Map polygons
// ---------------------------------------------------------------------------

// Geometries are serialized once per payload: strings keep VMap's deep
// watcher cheap and let it cache parsed GeoJSON per uuid.
const elementGeometryStrings = computed(() => {
    const geometryStrings = new Map();
    for (const element of elements.value) {
        if (element?.id && element?.polygon) {
            geometryStrings.set(element.id, JSON.stringify(element.polygon));
        }
    }
    return geometryStrings;
});

const referentGeometryString = computed(() => (
    referent.value?.polygon ? JSON.stringify(referent.value.polygon) : null
));

const boundsGeometryStrings = computed(() => {
    const entries = [];

    if (referentGeometryString.value) {
        entries.push(referentGeometryString.value);
    }

    entries.push(...elementGeometryStrings.value.values());

    return entries;
});

function isLineElement(element) {
    return element?.style?.render_as === 'line'
        || String(element?.geometry_type ?? '').includes('LineString');
}

function elementVisualState(element) {
    if (element.id === selectedElementId.value) {
        return 'sel';
    }

    if (hasActiveFilters.value && !activeClassificationKeys.value.has(elementClassificationKey(element))) {
        return 'dim';
    }

    return 'base';
}

function referentLeafletStyle(style) {
    return {
        color: style?.stroke_color ?? '#929085',
        weight: Number(style?.stroke_weight) || 1,
        opacity: Number(style?.stroke_opacity ?? 1),
        fillColor: style?.fill_color ?? '#929085',
        fillOpacity: Number(style?.fill_opacity ?? 0.2),
    };
}

function elementLeafletStyle(element, state) {
    const style = element.style ?? {};
    const line = isLineElement(element);
    const base = {
        color: style.stroke_color ?? '#888888',
        weight: Number(style.stroke_weight) || (line ? 4 : 2),
        opacity: Number(style.stroke_opacity ?? 1),
        fillColor: style.fill_color ?? style.stroke_color ?? '#888888',
        fillOpacity: line ? 0 : Number(style.fill_opacity ?? 0.5),
    };

    if (state === 'dim') {
        return {
            ...base,
            opacity: 0.12,
            fillOpacity: base.fillOpacity * 0.12,
        };
    }

    if (state === 'sel') {
        return {
            ...base,
            weight: base.weight + 3,
            opacity: 1,
            fillOpacity: line ? 0 : Math.min(base.fillOpacity + 0.25, 1),
        };
    }

    return base;
}

function elementTooltip(element) {
    const classificationName = element.status?.classification?.name;
    return [element.code ?? element.name, classificationName].filter(Boolean).join(' · ');
}

const selectedElement = computed(() => (
    elements.value.find((element) => element.id === selectedElementId.value) ?? null
));

// Defect point of the latest horizontal (drone) monitoring of the selection.
const selectedDefectPointDescriptor = computed(() => {
    const monitoring = selectedElement.value?.latest_monitoring;
    const detail = monitoring?.detail;

    if (monitoring?.kind !== 'horizontal' || !detail?.polygon) {
        return null;
    }

    const marker = detail.marker ?? {};

    return {
        uuid: `uc-monitoring-defect:${monitoring.id}`,
        polygon: JSON.stringify(detail.polygon),
        notInteractive: true,
        style: {
            radius: Number(marker.radius_px) || 6,
            fillColor: marker.fill_color ?? '#519ACF',
            fillOpacity: Number(marker.fill_opacity ?? 1),
            color: marker.stroke_color ?? '#FFFFFF',
            opacity: Number(marker.stroke_opacity ?? 1),
            weight: Number(marker.stroke_weight) || 1,
        },
    };
});

const mapPolygons = computed(() => {
    if (!payload.value) {
        return [];
    }

    const descriptors = [];

    if (referentGeometryString.value) {
        descriptors.push({
            uuid: 'uc-monitoring-boundary',
            polygon: referentGeometryString.value,
            notInteractive: true,
            style: referentLeafletStyle(referent.value?.style),
        });
    }

    // Areas first so lines stay on top and remain clickable.
    const orderedElements = [...elements.value]
        .sort((a, b) => Number(isLineElement(a)) - Number(isLineElement(b)));

    for (const element of orderedElements) {
        const geometryString = elementGeometryStrings.value.get(element.id);
        if (!geometryString) {
            continue;
        }

        const state = elementVisualState(element);
        const style = elementLeafletStyle(element, state);

        descriptors.push({
            uuid: `uc-monitoring-el:${element.id}:${state}`,
            polygon: geometryString,
            style,
            ...(state !== 'dim' ? {
                hoverStyle: {
                    ...style,
                    weight: style.weight + 2,
                    opacity: 1,
                },
            } : {}),
            tooltip: elementTooltip(element),
            onClick: () => toggleElementSelection(element.id),
        });
    }

    if (selectedDefectPointDescriptor.value) {
        descriptors.push(selectedDefectPointDescriptor.value);
    }

    return descriptors;
});

function toggleElementSelection(elementId) {
    selectedElementId.value = selectedElementId.value === elementId ? null : elementId;
}

function clearSelection() {
    selectedElementId.value = null;
}

// ---------------------------------------------------------------------------
// Map sizing / fitting (the tab is kept mounted via v-show)
// ---------------------------------------------------------------------------

function cancelScheduledRefresh() {
    if (!refreshFrameId) {
        return;
    }

    cancelAnimationFrame(refreshFrameId);
    refreshFrameId = 0;
}

function disableMapUserNavigation() {
    const map = mapEl.value?.map;
    if (!map) {
        return;
    }

    map.dragging?.disable?.();
    map.scrollWheelZoom?.disable?.();
    map.doubleClickZoom?.disable?.();
    map.boxZoom?.disable?.();
    map.keyboard?.disable?.();
    map.touchZoom?.disable?.();
    map.zoomControl?.remove?.();
}

function blockWheelZoom(event) {
    event.preventDefault();
    event.stopPropagation();
}

function observeWheelZoom() {
    if (!containerEl.value || removeWheelBlocker) {
        return;
    }

    containerEl.value.addEventListener('wheel', blockWheelZoom, {
        capture: true,
        passive: false,
    });

    removeWheelBlocker = () => {
        containerEl.value?.removeEventListener('wheel', blockWheelZoom, {
            capture: true,
        });
        removeWheelBlocker = null;
    };
}

function scheduleMapRefresh({ fitBounds = false } = {}) {
    if (typeof window === 'undefined') {
        return;
    }

    cancelScheduledRefresh();

    refreshFrameId = requestAnimationFrame(() => {
        refreshFrameId = requestAnimationFrame(() => {
            const host = containerEl.value ?? mapEl.value?.$el;
            const rect = host?.getBoundingClientRect?.();

            if (!host || !mapEl.value || !rect || rect.width <= 0 || rect.height <= 0) {
                return;
            }

            disableMapUserNavigation();
            invalidateSize();

            if ((fitBounds || !hasFittedVisibleBounds.value) && boundsGeometryStrings.value.length) {
                fitToGeoJsonBounds(boundsGeometryStrings.value, {
                    padding: MONITORING_BOUNDS_FIT_PADDING,
                });
                hasFittedVisibleBounds.value = true;
            }
        });
    });
}

function observeMapContainer() {
    if (typeof ResizeObserver === 'undefined' || !containerEl.value) {
        return;
    }

    resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry || entry.contentRect.width <= 0 || entry.contentRect.height <= 0) {
            return;
        }

        scheduleMapRefresh();
    });

    resizeObserver.observe(containerEl.value);
}

watch(() => props.active, (isActive) => {
    if (isActive) {
        ensureFetched();
        nextTick(() => scheduleMapRefresh());
    }
});

onMounted(() => {
    observeMapContainer();
    nextTick(() => {
        observeWheelZoom();
        disableMapUserNavigation();
    });

    if (props.active) {
        ensureFetched();
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    removeWheelBlocker?.();

    if (typeof window !== 'undefined') {
        cancelScheduledRefresh();
    }
});

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

function formatDate(isoString) {
    if (!isoString) {
        return '—';
    }

    const date = new Date(isoString);
    return Number.isNaN(date.getTime())
        ? '—'
        : formatLocalizedDate(date, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(isoString) {
    if (!isoString) {
        return '—';
    }

    const date = new Date(isoString);
    return Number.isNaN(date.getTime())
        ? '—'
        : formatLocalizedDate(date, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
}

function formatNumber(value, maximumFractionDigits = 2) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }

    return formatLocalizedNumber(value, { maximumFractionDigits });
}

function badgeTextColor(hexColor) {
    const hex = String(hexColor ?? '').replace('#', '');
    if (hex.length !== 6) {
        return '#1f2937';
    }

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

    return luminance > 0.6 ? '#1f2937' : '#ffffff';
}

function badgeStyle(color) {
    return {
        backgroundColor: color ?? '#A6A6A6',
        color: badgeTextColor(color),
    };
}

function monitoringKindLabel(kind) {
    return ['punctual', 'horizontal'].includes(kind)
        ? t(`pompei.monitoring.kinds.${kind}`)
        : kind ?? '—';
}

const selectedMonitoring = computed(() => selectedElement.value?.latest_monitoring ?? null);
const selectedMonitoringDetail = computed(() => selectedMonitoring.value?.detail ?? null);
const isSelectedMonitoringPunctual = computed(() => selectedMonitoring.value?.kind === 'punctual');
const isSelectedMonitoringHorizontal = computed(() => selectedMonitoring.value?.kind === 'horizontal');

// Punctual indices merged per structural component: IA / IFR / DR side by side.
const selectedIndicesRows = computed(() => {
    const indices = selectedMonitoringDetail.value?.indices;
    if (!indices) {
        return [];
    }

    const rows = new Map();
    const ensureRow = (specificElement) => {
        const key = specificElement?.id ?? specificElement?.name ?? 'unknown';
        if (!rows.has(key)) {
            rows.set(key, {
                key,
                name: specificElement?.name ?? '—',
                ia: null,
                ifr: null,
                dr: null,
            });
        }
        return rows.get(key);
    };

    for (const entry of indices.ia ?? []) {
        ensureRow(entry.specific_element).ia = entry;
    }
    for (const entry of indices.ifr ?? []) {
        ensureRow(entry.specific_element).ifr = entry;
    }
    for (const entry of indices.dr ?? []) {
        ensureRow(entry.specific_element).dr = entry;
    }

    return [...rows.values()];
});

const selectedDegradations = computed(() => selectedMonitoringDetail.value?.degradations ?? []);

const selectedMonitoringPhotos = computed(() => selectedMonitoring.value?.photos ?? []);

const pageTitle = computed(() => {
    const label = referent.value?.code ?? referent.value?.name ?? t('pompei.monitoring.page.fallbackTitle');
    const lastMonitoringDate = formatDate(summary.value?.last_monitored_at);

    return lastMonitoringDate === '—'
        ? label
        : t('pompei.monitoring.page.lastMonitoring', {
            label,
            date: lastMonitoringDate.replace(/\//g, '-'),
        });
});

const summaryMetricRows = computed(() => [
    { label: t('pompei.monitoring.summary.total'), value: summary.value?.elements_total },
    { label: t('pompei.monitoring.summary.monitored'), value: summary.value?.elements_monitored },
    { label: t('pompei.monitoring.summary.notMonitored'), value: summary.value?.elements_not_monitored },
]);

function formatSummaryCount(value) {
    const formatted = formatNumber(value, 0);
    return formatted === '—' ? '(—)' : `(${formatted})`;
}

function formatMonitoringKindCount(count, kind) {
    return t(`pompei.monitoring.kinds.${kind}Count`, {
        count: formatNumber(count, 0),
    }, Number(count));
}

const monitoringNarrativeTitle = computed(() => t('pompei.monitoring.narrative.title'));

const monitoringNarrativeParagraphs = computed(() => [
    t('pompei.monitoring.narrative.description'),
]);

const monitoringKindCountsLabel = computed(() => {
    const kinds = summary.value?.latest_monitoring_kinds ?? {};
    const parts = [];

    if (kinds.punctual) {
        parts.push(formatMonitoringKindCount(kinds.punctual, 'punctual'));
    }
    if (kinds.horizontal) {
        parts.push(formatMonitoringKindCount(kinds.horizontal, 'horizontal'));
    }

    return parts.join(' · ');
});
</script>

<template>
    <section class="pompei-uc-monitoring flex flex-col gap-3 pt-4">
        <div v-if="!compositeId"
            class="flex items-center justify-center rounded-[var(--rounded-btn)] bg-[#f4f8f7] px-3 py-10 text-[13px] text-[var(--color-principal)]">
            {{ t('pompei.monitoring.empty.referent') }}
        </div>

        <div v-else-if="loading && !payload" role="status" aria-live="polite"
            class="flex items-center justify-center gap-3 rounded-[var(--rounded-btn)] px-3 py-10 text-[13px] text-[var(--color-principal)]">
            <Icon name="spinner" shared :size="16" color="var(--color-principal)" class="h-4 w-4 animate-spin-fast"
                aria-hidden="true" />
            <span class="font-normal">{{ t('pompei.monitoring.loading.initial') }}</span>
        </div>

        <div v-else-if="error"
            class="flex flex-wrap items-center justify-center gap-3 rounded-[var(--rounded-btn)] px-3 py-10 text-[13px] text-[var(--color-principal)]">
            <span>{{ t(error) }}</span>
            <button type="button"
                class="rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure,#00836f)] px-4 py-1 text-[12px] font-bold uppercase tracking-wider text-white"
                @click="fetchSummary">
                {{ t('pompei.monitoring.actions.retry') }}
            </button>
        </div>

        <div v-else-if="!payload"
            class="flex items-center justify-center rounded-[var(--rounded-btn)] bg-[#f4f8f7] px-3 py-10 text-[13px] text-[var(--color-principal)]">
            {{ t('pompei.monitoring.empty.data') }}
        </div>

        <template v-else>
            <header class="py-3">
                <h3 class="m-0 truncate text-[13px] font-medium text-[#3f3f3f]">
                    {{ pageTitle }}
                </h3>
            </header>

            <div class="grid min-h-0 grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_30%]">
                <div ref="containerEl"
                    class="pompei-uc-monitoring-map border-t relative h-[clamp(500px,72vh,780px)] min-h-0 overflow-hidden bg-[#f2f1ed]"
                    @wheel.capture.prevent.stop>
                    <VMap ref="mapEl" class="h-full w-full pompei-native-layer-control" :leafletConfig="leafletConfig"
                        :polygons="mapPolygons" :customLayers="mapLayers" :center="mapCenter" :fitBounds="true"
                        :defaultFitPolygonBounds="false" :minPointsZoom="10" :currentLayer="selectedLayerIndex"
                        :layerControl="showLayerControl" :zoomControl="false" @layer:change="changeLayerByIndex" />

                    <div v-if="loading"
                        class="absolute inset-0 z-[1001] grid place-items-center bg-white/70 text-[13px] text-[var(--color-principal)]">
                        {{ t('pompei.monitoring.loading.updating') }}
                    </div>

                    <article v-if="selectedElement"
                        class="absolute bottom-3 right-3 top-3 z-[1000] flex w-[330px] max-w-[88%] flex-col overflow-hidden rounded-[var(--rounded-btn)] border border-[#d6d6d6] bg-white/95 shadow-[0_3px_12px_rgba(0,0,0,0.22)] backdrop-blur-sm">
                        <header class="flex items-start justify-between gap-2 border-b border-[#e7e7e7] p-3">
                            <div class="min-w-0">
                                <p class="m-0 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                                    {{ selectedElement.type?.name ?? t('pompei.monitoring.detail.element') }}
                                </p>
                                <h4 class="m-0 truncate text-[14px] font-bold text-[var(--color-principal)]">
                                    {{ selectedElement.code ?? selectedElement.name }}
                                </h4>
                            </div>
                            <button type="button"
                                class="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[16px] leading-none text-neutral-500 hover:bg-neutral-100"
                                :aria-label="t('pompei.monitoring.actions.closeDetail')" @click="clearSelection">
                                ×
                            </button>
                        </header>

                        <div
                            class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 text-[12px] text-neutral-700">
                            <div class="flex flex-wrap items-center gap-2">
                                <span v-if="selectedElement.status?.classification"
                                    class="rounded-full px-2.5 py-0.5 text-[12px] font-bold uppercase tracking-wider"
                                    :style="badgeStyle(selectedElement.status.classification.color)">
                                    {{ selectedElement.status.classification.name }}
                                </span>
                                <span class="text-[12px] text-neutral-500">
                                    {{ selectedElement.status?.state === 'monitored'
                                        ? t('pompei.monitoring.states.monitored')
                                        : t('pompei.monitoring.states.notMonitored')
                                    }}
                                    <template v-if="selectedElement.status?.last_monitored_at">
                                        {{ t('pompei.monitoring.states.onDate', { date: formatDate(selectedElement.status.last_monitored_at) }) }}
                                    </template>
                                </span>
                            </div>

                            <p v-if="!selectedMonitoring" class="m-0 text-neutral-500">
                                {{ t('pompei.monitoring.empty.element') }}
                            </p>

                            <template v-else>
                                <div class="rounded-[var(--rounded-btn)] bg-[#f4f8f7] p-2.5">
                                    <h5
                                        class="m-0 mb-1 text-[12px] font-bold uppercase tracking-wider text-[var(--color-principal)]">
                                        {{ t('pompei.monitoring.detail.latest') }}
                                    </h5>
                                    <dl class="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5">
                                        <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.type') }}</dt>
                                        <dd class="m-0 font-medium">{{ monitoringKindLabel(selectedMonitoring.kind) }}
                                        </dd>
                                        <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.date') }}</dt>
                                        <dd class="m-0 font-medium">{{ formatDateTime(selectedMonitoring.performed_at)
                                        }}</dd>
                                        <template v-if="selectedMonitoringDetail?.project?.name">
                                            <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.project') }}</dt>
                                            <dd class="m-0 font-medium">{{ selectedMonitoringDetail.project.name }}</dd>
                                        </template>
                                        <template v-if="selectedMonitoringDetail?.user?.name">
                                            <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.operator') }}</dt>
                                            <dd class="m-0 font-medium">{{ selectedMonitoringDetail.user.name }}</dd>
                                        </template>
                                        <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.status') }}</dt>
                                        <dd class="m-0 font-medium">
                                            {{ selectedMonitoring.validated
                                                ? t('pompei.monitoring.states.validated')
                                                : t('pompei.monitoring.states.notValidated') }}
                                        </dd>
                                    </dl>
                                    <p v-if="selectedMonitoringDetail?.not_detectable"
                                        class="m-0 mt-1 font-bold text-[#9b2d20]">
                                        {{ t('pompei.monitoring.states.notDetectable') }}
                                    </p>
                                </div>

                                <div v-if="isSelectedMonitoringPunctual && selectedIndicesRows.length">
                                    <h5
                                        class="m-0 mb-1.5 text-[12px] font-bold uppercase tracking-wider text-[var(--color-principal)]">
                                        {{ t('pompei.monitoring.detail.indices') }}
                                    </h5>
                                    <table class="w-full border-collapse text-[12px]">
                                        <thead>
                                            <tr class="text-left text-[10px] uppercase tracking-wider text-neutral-500">
                                                <th class="py-1 pr-1 font-bold">{{ t('pompei.monitoring.detail.component') }}</th>
                                                <th class="px-1 py-1 font-bold">IA</th>
                                                <th class="px-1 py-1 font-bold">IFR</th>
                                                <th class="py-1 pl-1 text-right font-bold">DR</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in selectedIndicesRows" :key="row.key"
                                                class="border-t border-[#ececec] align-middle">
                                                <td class="py-1.5 pr-1">{{ row.name }}</td>
                                                <td class="px-1 py-1.5">
                                                    <span v-if="row.ia"
                                                        class="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold"
                                                        :style="badgeStyle(row.ia.color)">
                                                        {{ row.ia.name }}
                                                    </span>
                                                    <span v-else>—</span>
                                                </td>
                                                <td class="px-1 py-1.5">
                                                    <span v-if="row.ifr"
                                                        class="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold"
                                                        :style="badgeStyle(row.ifr.color)">
                                                        {{ row.ifr.name }}
                                                    </span>
                                                    <span v-else>—</span>
                                                </td>
                                                <td class="py-1.5 pl-1 text-right font-medium">
                                                    {{ formatNumber(row.dr?.value) }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-if="isSelectedMonitoringPunctual && selectedDegradations.length">
                                    <h5
                                        class="m-0 mb-1.5 text-[12px] font-bold uppercase tracking-wider text-[var(--color-principal)]">
                                        {{ t('pompei.monitoring.detail.degradations', { count: selectedDegradations.length }) }}
                                    </h5>
                                    <ul class="m-0 flex list-none flex-col gap-2 p-0">
                                        <li v-for="degradation in selectedDegradations" :key="degradation.id"
                                            class="rounded-[var(--rounded-btn)] border border-[#ececec] p-2">
                                            <p class="m-0 font-bold">{{ degradation.type?.name ?? t('pompei.monitoring.detail.degradation') }}</p>
                                            <p class="m-0 text-[12px] text-neutral-500">
                                                {{ degradation.specific_element?.name }}
                                            </p>
                                            <p class="m-0 mt-0.5 text-[12px]">
                                                <template v-if="degradation.gravity?.name">{{ t('pompei.monitoring.detail.gravity', { value: degradation.gravity.name }) }}</template>
                                                <template
                                                    v-if="degradation.dr_contribution !== null && degradation.dr_contribution !== undefined">
                                                    · DR +{{ formatNumber(degradation.dr_contribution) }}
                                                </template>
                                                <template v-if="degradation.square_meters">
                                                    · {{ formatNumber(degradation.square_meters) }} m²
                                                </template>
                                            </p>
                                            <p v-if="degradation.annotations"
                                                class="m-0 mt-0.5 text-[12px] italic text-neutral-500">
                                                {{ degradation.annotations }}
                                            </p>
                                            <div v-if="degradation.photos?.length"
                                                class="mt-1.5 flex flex-wrap gap-1.5">
                                                <a v-for="photo in degradation.photos" :key="photo.id" :href="photo.url"
                                                    target="_blank" rel="noopener noreferrer"
                                                    class="block h-12 w-12 overflow-hidden rounded border border-[#ececec]"
                                                    :title="photo.name">
                                                    <img :src="photo.preview_url ?? photo.url"
                                                        :alt="photo.name ?? t('pompei.monitoring.detail.degradationPhoto')"
                                                        class="h-full w-full object-cover" loading="lazy"
                                                        @error="(event) => { event.target.closest('a').style.display = 'none'; }" />
                                                </a>
                                            </div>
                                        </li>
                                    </ul>
                                </div>

                                <div v-if="isSelectedMonitoringHorizontal" class="flex flex-col gap-1.5">
                                    <h5
                                        class="m-0 text-[12px] font-bold uppercase tracking-wider text-[var(--color-principal)]">
                                        {{ t('pompei.monitoring.detail.droneSurvey') }}
                                    </h5>
                                    <dl class="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5">
                                        <template v-if="selectedMonitoringDetail?.degradation_type?.name">
                                            <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.degradationType') }}</dt>
                                            <dd class="m-0 font-medium">{{
                                                selectedMonitoringDetail.degradation_type.name }}</dd>
                                        </template>
                                        <template v-if="selectedMonitoringDetail?.intensity">
                                            <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.intensity') }}</dt>
                                            <dd class="m-0">
                                                <span
                                                    class="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold"
                                                    :style="badgeStyle(selectedMonitoringDetail.intensity.color)">
                                                    {{ selectedMonitoringDetail.intensity.name }}
                                                </span>
                                            </dd>
                                        </template>
                                        <template v-if="selectedMonitoringDetail?.drone_flight">
                                            <dt class="text-neutral-500">{{ t('pompei.monitoring.detail.flight') }}</dt>
                                            <dd class="m-0 font-medium">
                                                {{ selectedMonitoringDetail.drone_flight.name }}
                                                <template v-if="selectedMonitoringDetail.drone_flight.date">
                                                    ({{ formatDate(selectedMonitoringDetail.drone_flight.date) }})
                                                </template>
                                            </dd>
                                        </template>
                                    </dl>
                                    <p v-if="selectedMonitoringDetail?.observations"
                                        class="m-0 text-[12px] text-neutral-600">
                                        {{ selectedMonitoringDetail.observations }}
                                    </p>
                                    <p class="m-0 text-[12px] text-neutral-500">
                                        {{ t('pompei.monitoring.detail.defectPoint') }}
                                    </p>
                                </div>

                                <div v-if="selectedMonitoringPhotos.length" class="flex flex-wrap gap-1.5">
                                    <a v-for="photo in selectedMonitoringPhotos" :key="photo.id" :href="photo.url"
                                        target="_blank" rel="noopener noreferrer"
                                        class="block h-12 w-12 overflow-hidden rounded border border-[#ececec]"
                                        :title="photo.name">
                                        <img :src="photo.preview_url ?? photo.url"
                                            :alt="photo.name ?? t('pompei.monitoring.detail.monitoringPhoto')" class="h-full w-full object-cover"
                                            loading="lazy"
                                            @error="(event) => { event.target.closest('a').style.display = 'none'; }" />
                                    </a>
                                </div>
                            </template>
                        </div>
                    </article>
                </div>

                <aside
                    class="flex min-h-0 flex-col gap-7 border-t border-[#d8d8d8] pt-6 text-[#3f3f3f] overflow-scroll">
                    <section>
                        <dl class="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 text-[13px]">
                            <template v-for="row in summaryMetricRows" :key="row.label">
                                <dt class="font-medium">{{ row.label }}</dt>
                                <dd class="m-0 text-right font-medium">{{ formatSummaryCount(row.value)
                                }}</dd>
                            </template>
                        </dl>
                        <p v-if="monitoringKindCountsLabel" class="m-0 mt-1 text-[12px] text-neutral-500">
                            {{ monitoringKindCountsLabel }}
                        </p>
                    </section>

                    <section class="border-t border-[#d8d8d8] pt-7">
                        <div class="mb-6 flex items-center justify-between gap-3">
                            <h4 class="m-0 text-[14px] font-bold uppercase text-[#3f3f3f]">
                                {{ t('pompei.monitoring.narrative.fragility') }}
                            </h4>
                            <button v-if="hasActiveFilters" type="button"
                                class="text-[10px] font-bold uppercase text-[#3f3f3f] underline"
                                @click="clearClassificationFilters">
                                {{ t('pompei.monitoring.actions.reset') }}
                            </button>
                        </div>

                        <div class="mb-8 flex flex-col gap-3 text-[13px] leading-[1.55] text-[#3f3f3f]">
                            <p class="m-0">{{ monitoringNarrativeTitle }}</p>
                            <p v-for="paragraph in monitoringNarrativeParagraphs" :key="paragraph" class="m-0">
                                {{ paragraph }}
                            </p>
                        </div>

                        <ul class="m-0 flex list-none flex-col gap-3 p-0">
                            <li v-for="entry in classificationCounts" :key="entry.key">
                                <button type="button"
                                    class="flex w-full items-center gap-3 text-left text-[13px] transition-opacity hover:opacity-80"
                                    :class="activeClassificationKeys.has(entry.key)
                                        ? 'opacity-100'
                                        : hasActiveFilters ? 'opacity-40' : 'opacity-100'"
                                    :title="classificationDescriptions[entry.key] ?? ''"
                                    :aria-pressed="activeClassificationKeys.has(entry.key)"
                                    @click="toggleClassificationFilter(entry.key)">
                                    <span class="header_dropdown_sheet_color shrink-0 rounded-full"
                                        :style="{ backgroundColor: entry.color }" aria-hidden="true" />
                                    <span class="min-w-0 flex-1 truncate font-medium uppercase">
                                        {{ entry.name }}
                                    </span>
                                    <span class="shrink-0 font-medium">
                                        {{ formatSummaryCount(entry.count) }}
                                    </span>
                                </button>
                            </li>
                        </ul>
                    </section>
                </aside>
            </div>
        </template>
    </section>
</template>

<style scoped>
.pompei-uc-monitoring-map :deep(.leaflet-container) {
    background-color: #f2f1ed !important;
}

.pompei-uc-monitoring-map :deep(.leaflet-overlay-pane svg path) {
    mix-blend-mode: multiply;
}

.pompei-uc-monitoring-map :deep(.leaflet-control-layers) {
    margin-bottom: 24px !important;
    margin-left: 28px !important;
}
</style>